import { apiInstance } from '@/api/instance';
import {
  type PartnerEventAddress,
  type PartnerEventAddressBody,
} from '@/api/routes/partners/partners.events.api';

/** Contract: bodyprint-api/docs/external-apis/partners-contract.md (Ofertas). */
export type PartnerOfferStatus =
  'draft' | 'in_review' | 'changes_requested' | 'approved' | 'rejected' | 'archived';

/** What the user gets. Only coupons today. */
interface PartnerOfferBenefit {
  kind: 'coupon';
  /** Up to 30 characters. */
  code: string;
  /** The coupon's rules, up to 200 characters ("Mínimo de R$ 100"). The app shows them under the coupon. */
  terms?: string | null;
}

export type PartnerOfferActionKind = 'link' | 'whatsapp' | 'phone' | 'lead' | 'directions';

/**
 * One button of the offer, in button order. Without `label` the app and the portal show the
 * kind's default text. Numbers come back with digits only.
 */
export type PartnerOfferAction =
  | { kind: 'link'; url: string; label?: string | null }
  | { kind: 'whatsapp'; number: string; message?: string | null; label?: string | null }
  | { kind: 'phone'; number: string; label?: string | null }
  | { kind: 'lead'; label?: string | null }
  | { kind: 'directions'; label?: string | null };

/** A place where the offer is valid: an address row plus an optional short name. */
export type PartnerOfferLocation = PartnerEventAddress & { label: string | null };

export interface PartnerOffer {
  id: string;
  partnerId: string;
  title: string;
  body: string;
  imageUrl: string | null;
  benefit: PartnerOfferBenefit | null;
  /** 0 to 2. Without actions the offer has a coupon. */
  actions: PartnerOfferAction[];
  /** 0 to 20, in the order the partner wrote them. Required with a `directions` action. */
  locations: PartnerOfferLocation[];
  desiredAudienceText: string | null;
  desiredStartsAt: string | null;
  desiredEndsAt: string | null;
  status: PartnerOfferStatus;
  reviewNote: string | null;
  submittedAt: string | null;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PartnerOffersListParams {
  status?: PartnerOfferStatus;
  offset?: number;
  limit?: number;
}

/** Offers per tab, regardless of the list's filter. */
export type PartnerOfferStatusCounts = Record<PartnerOfferStatus | 'all', number>;

export interface PartnerOffersListResponse {
  items: PartnerOffer[];
  count: number;
  statusCounts: PartnerOfferStatusCounts;
}

export type PartnerOfferLocationBody = PartnerEventAddressBody & { label?: string | null };

/** Body of POST and PUT. Saving replaces the whole list of locations. */
export type PartnerOfferBody = Pick<PartnerOffer, 'title' | 'body' | 'actions'> &
  Partial<
    Pick<
      PartnerOffer,
      'imageUrl' | 'benefit' | 'desiredAudienceText' | 'desiredStartsAt' | 'desiredEndsAt'
    >
  > & { locations: PartnerOfferLocationBody[] };

export interface PartnerOfferResponse {
  offer: PartnerOffer;
}

export const partnersOffersApi = {
  list: (params: PartnerOffersListParams) =>
    apiInstance.get<PartnerOffersListResponse>('partners/offers', { params }),
  read: (id: string) => apiInstance.get<PartnerOfferResponse>(`partners/offers/${id}`),
  create: (data: PartnerOfferBody) =>
    apiInstance.post<PartnerOfferResponse>('partners/offers', data),
  update: (id: string, data: PartnerOfferBody) =>
    apiInstance.put<PartnerOfferResponse>(`partners/offers/${id}`, data),
  submit: (id: string) => apiInstance.post<PartnerOfferResponse>(`partners/offers/${id}/submit`),
  archive: (id: string) => apiInstance.post<PartnerOfferResponse>(`partners/offers/${id}/archive`),
};

export const partnersOffersApiKeys = {
  all: ['offers'] as const,
  list: (params: PartnerOffersListParams) => ['offers', 'list', params] as const,
  detail: (id: string) => ['offers', 'detail', id] as const,
};
